import { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowUpFromLine, LoaderCircle } from 'lucide-react'

import { StatusPill } from '@/components/px/StatusPill'
import { Button } from '@/components/ui/button'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'
import { BOARD_FOCUS_RING } from '../focusRing'
import {
  pullToBoard,
  selectEpicTitle,
  selectPullError,
  selectPullingTaskId,
  selectSelectedTaskId,
  taskSelected
} from '../slices/boardSlice'
import type { BoardColumnKey, BoardTaskRow, KeyedFeatureError } from '../types'

export interface TaskCardProps {
  task: BoardTaskRow
  column: BoardColumnKey
  /** The project the register link targets — the route param, or `selectBoardProjectId` on /my. */
  projectId: string
  /** Whether the caller's role may open the requirements register (`roleAccess.canAccess`). */
  canOpenRegister: boolean
  /** Whether Pull to board renders on a BACKLOG card (`selectCanPullToBoard`: this project, WRITE). */
  canPull: boolean
}

/** The copy a refused pull shows: 404 = someone pulled it first, 403 = no write access. */
const pullErrorKey = (error: KeyedFeatureError): string =>
  error.status === 404 ? 'board.pull.errors.gone' : error.status === 403 ? 'board.pull.errors.forbidden' : 'board.pull.errors.generic'

/**
 * One card of the Tasks view (CR-BUILD-BOARD-EPICS-2026-09-26). The title is
 * the ONLY interactive element that selects the task; the requirement-ref
 * links are its siblings, never nested inside the select button (AC-20 /
 * spec R.6.7). The phase chip is on Build-queue cards only; Backlog cards say
 * whether they are backlog or to do.
 *
 * `build-queue-task-<id>` stays on the SELECT button, exactly where the
 * shipped queue carried it (tests and the visual lens click it); the card
 * itself is `task-card-<id>`.
 *
 * Pull to board (fix cycle 1, reverses Decision D5 — Agon 2026-09-26): a
 * BACKLOG card offers one outline button, absent (never disabled) unless the
 * caller may write this project; disabled only while its own pull is in
 * flight. The card moves when the server answers (`pullToBoard`), never
 * before; a refusal is shown inline on this card.
 */
export function TaskCard({ task, column, projectId, canOpenRegister, canPull }: TaskCardProps) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const selected = useAppSelector(selectSelectedTaskId) === task.legacyId
  const epicTitle = useAppSelector((s) => selectEpicTitle(s, task.epicId))
  const orgId = useAppSelector(selectActiveOrgId)
  const pulling = useAppSelector(selectPullingTaskId) === task.legacyId
  const pullError = useAppSelector(selectPullError)
  const { generatedFromRequirementRefs: refs = [] } = task
  const pullable = canPull && column === 'backlog' && task.status === 'backlog'
  const refusal = pullError !== null && pullError.scope === task.legacyId ? pullError : null

  const onSelectClicked = useCallback(() => {
    dispatch(taskSelected(task.legacyId))
  }, [dispatch, task.legacyId])

  const onPullClicked = useCallback(() => {
    if (orgId === null) return
    void dispatch(pullToBoard({ orgId, projectId, taskId: task.legacyId }))
  }, [dispatch, orgId, projectId, task.legacyId])

  const chip =
    column === 'buildQueue' ? (
      <StatusPill tone={task.status === 'blocked' ? 'blocked' : 'neutral'}>
        <span data-testid={`task-phase-${task.legacyId}`} data-status={task.status}>
          {t(`board.phase.${task.status}`)}
        </span>
      </StatusPill>
    ) : column === 'backlog' ? (
      <StatusPill tone="neutral">
        <span data-testid={`task-state-${task.legacyId}`} data-status={task.status}>
          {t(`board.status.${task.status}`)}
        </span>
      </StatusPill>
    ) : null

  return (
    <article
      data-testid={`task-card-${task.legacyId}`}
      className={cn('border-b border-hairline px-3 py-2.5 last:border-0', selected && 'bg-accent')}
    >
      <button
        type="button"
        onClick={onSelectClicked}
        aria-current={selected ? 'true' : undefined}
        data-testid={`build-queue-task-${task.legacyId}`}
        className={cn('group block w-full rounded text-left', BOARD_FOCUS_RING)}
      >
        <span
          className={cn(
            'block text-sm leading-snug text-foreground [overflow-wrap:anywhere] group-hover:underline group-hover:underline-offset-2',
            selected && 'font-medium text-accent-foreground'
          )}
        >
          {task.title}
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {epicTitle ?? task.epicId ?? t('board.epics.unassigned')}
        </span>
      </button>
      {(chip !== null || refs.length > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {chip}
          {refs.length > 0 && (
            <span className="inline-flex flex-wrap items-center gap-1 text-[0.6875rem] text-muted-foreground">
              <span aria-hidden>{t('board.refs.prefix')}</span>
              <ul aria-label={t('board.refs.label')} className="inline-flex flex-wrap items-center gap-1">
                {refs.map((ref) => (
                  <li key={ref}>
                    {canOpenRegister ? (
                      <Link
                        to={{ pathname: `/project/${projectId}/requirements`, search: `?ref=${encodeURIComponent(ref)}` }}
                        className={cn(
                          'rounded border border-border bg-card px-1 font-mono text-[0.6875rem] text-accent-foreground underline decoration-dotted underline-offset-2 hover:bg-muted hover:decoration-solid',
                          BOARD_FOCUS_RING
                        )}
                      >
                        {ref}
                      </Link>
                    ) : (
                      <span className="rounded border border-border bg-card px-1 font-mono text-[0.6875rem] text-ink-soft">
                        {ref}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </span>
          )}
        </div>
      )}
      {pullable && (
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={pulling}
            aria-busy={pulling || undefined}
            aria-label={t('board.pull.label', { title: task.title })}
            onClick={onPullClicked}
            data-testid={`pull-to-board-${task.legacyId}`}
            className={cn('h-7 gap-1.5 px-2 text-xs', BOARD_FOCUS_RING)}
          >
            {pulling ? (
              <LoaderCircle aria-hidden className="size-3.5 animate-spin" />
            ) : (
              <ArrowUpFromLine aria-hidden className="size-3.5" />
            )}
            {t('board.pull.action')}
          </Button>
          {refusal !== null && (
            <p role="alert" data-testid={`pull-error-${task.legacyId}`} className="mt-1 text-xs text-blocked">
              {t(pullErrorKey(refusal))}
              {refusal.code !== undefined && refusal.code !== '' && (
                <span className="ml-1 font-mono text-muted-foreground">{refusal.code}</span>
              )}
            </p>
          )}
        </div>
      )}
    </article>
  )
}
